import { useState, useEffect } from 'react';
import api, { membersApi } from '../api/client';

// ── Design tokens (matching EstateTax.tsx) ──────────────────
const C = {
  ivory: '#FAF7F2', cream: '#F3EDE4', parchment: '#EBE3D6',
  navy: '#1B2A4A', navyLight: '#2A3D66', navyMuted: '#3A5080',
  burgundy: '#7A2E3B', burgundyMuted: 'rgba(122,46,59,0.12)',
  gold: '#B8953E', goldLight: '#D4AF5C', goldMuted: 'rgba(184,149,62,0.15)',
  hunter: '#2D5F4A', hunterMuted: 'rgba(45,95,74,0.12)',
  ink: '#1A1A1A', textBody: '#3D3830', textMuted: '#7A7265', textLight: '#A69D91',
  border: '#D6CCBE', borderLight: '#E8E0D4', borderDark: '#C4B8A8',
};
const fonts = {
  display: "'Cormorant Garamond', Georgia, serif",
  body: "'Libre Baskerville', Georgia, serif",
  mono: "'IBM Plex Mono', 'Courier New', monospace",
  sans: "'Lato', 'Helvetica Neue', sans-serif",
};

// ── Shared UI ───────────────────────────────────────────────
const Card = ({ children, style: s = {} }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <div style={{ background: C.ivory, border: `1px solid ${C.border}`, borderRadius: 3, ...s }}>{children}</div>
);
const PageHeader = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <div style={{ marginBottom: 28, borderBottom: `1px solid ${C.border}`, paddingBottom: 16 }}>
    <h1 style={{ fontSize: 28, fontFamily: fonts.display, fontWeight: 600, color: C.ink, margin: 0 }}>{title}</h1>
    <p style={{ fontSize: 14, fontFamily: fonts.body, color: C.textMuted, margin: '6px 0 0', fontStyle: 'italic' }}>{subtitle}</p>
  </div>
);
const SectionTitle = ({ children, sub }: { children: React.ReactNode; sub?: string }) => (
  <div style={{ marginBottom: 18 }}>
    <h2 style={{ fontSize: 13, fontWeight: 700, color: C.textMuted, fontFamily: fonts.sans, letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0, borderBottom: `2px solid ${C.navy}`, display: 'inline-block', paddingBottom: 4 }}>{children}</h2>
    {sub && <p style={{ fontSize: 13, color: C.textLight, margin: '6px 0 0', fontFamily: fonts.body, fontStyle: 'italic' }}>{sub}</p>}
  </div>
);
const Stat = ({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) => (
  <div style={{ padding: '18px 22px', borderRight: `1px solid ${C.borderLight}`, flex: 1, minWidth: 140 }}>
    <div style={{ fontSize: 10, color: C.textLight, letterSpacing: '0.1em', fontFamily: fonts.sans, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 600, color: accent || C.ink, fontFamily: fonts.display, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6, fontFamily: fonts.sans }}>{sub}</div>}
  </div>
);
const TH = ({ children, align = 'left' }: { children?: React.ReactNode; align?: string }) => (
  <th style={{ fontSize: 10, fontWeight: 700, color: C.textLight, fontFamily: fonts.sans, letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: align as any, padding: '10px 14px', borderBottom: `2px solid ${C.borderDark}`, background: C.cream }}>{children}</th>
);
const TD = ({ children, mono, accent, align = 'left', bold }: { children?: React.ReactNode; mono?: boolean; accent?: string; align?: string; bold?: boolean }) => (
  <td style={{ fontSize: 13, color: accent || C.textBody, padding: '11px 14px', borderBottom: `1px solid ${C.borderLight}`, fontFamily: mono ? fonts.mono : fonts.sans, fontWeight: bold ? 600 : 400, textAlign: align as any }}>{children}</td>
);
const StatusPill = ({ status, label }: { status: string; label: string }) => {
  const m: Record<string, { bg: string; fg: string }> = {
    current: { bg: C.hunterMuted, fg: C.hunter },
    watch: { bg: C.goldMuted, fg: C.gold },
    danger: { bg: C.burgundyMuted, fg: C.burgundy },
    info: { bg: 'rgba(42,61,102,0.1)', fg: C.navy },
    none: { bg: C.cream, fg: C.textLight },
  };
  const t = m[status] || m.none;
  return <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 2, fontSize: 10, fontWeight: 700, fontFamily: fonts.sans, letterSpacing: '0.06em', textTransform: 'uppercase', background: t.bg, color: t.fg }}>{label}</span>;
};
const AlertBox = ({ type, title, children }: { type: string; title?: string; children: React.ReactNode }) => {
  const s: Record<string, any> = {
    danger: { bg: C.burgundyMuted, border: C.burgundy, fg: C.burgundy },
    warning: { bg: C.goldMuted, border: C.gold, fg: C.gold },
    success: { bg: C.hunterMuted, border: C.hunter, fg: C.hunter },
    info: { bg: 'rgba(42,61,102,0.08)', border: C.navy, fg: C.navy },
  };
  const t = s[type] ?? s.info;
  return (
    <div style={{ padding: '14px 18px', background: t.bg, borderRadius: 3, borderLeft: `3px solid ${t.border}`, marginBottom: 10 }}>
      {title && <div style={{ fontFamily: fonts.sans, fontSize: 13, fontWeight: 700, color: t.fg, marginBottom: 4 }}>{title}</div>}
      <div style={{ fontFamily: fonts.body, fontSize: 12, color: C.textBody, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
};

// ── Helpers ──────────────────────────────────────────────────
function calcAge(dob: string | null): number | null {
  if (!dob) return null;
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function relationshipLabel(r: string): string {
  const map: Record<string, string> = {
    patriarch: 'Patriarch', matriarch: 'Matriarch', spouse: 'Spouse',
    child: 'Child', grandchild: 'Grandchild', sibling: 'Sibling',
    niece_nephew: 'Niece/Nephew', other: 'Other',
  };
  return map[r] || r?.charAt(0).toUpperCase() + r?.slice(1) || '—';
}

function generationLabel(gen: number): string {
  const map: Record<number, string> = { 1: 'G1 — Principals', 2: 'G2 — Children', 3: 'G3 — Grandchildren' };
  return map[gen] || `Generation ${gen}`;
}

// PA inheritance tax class based on relationship
function paClass(relationship: string): { cls: string; rate: string; color: string } {
  switch (relationship) {
    case 'spouse': return { cls: 'Exempt', rate: '0%', color: C.hunter };
    case 'child': case 'grandchild': return { cls: 'Direct Descendant', rate: '4.5%', color: C.navy };
    case 'sibling': return { cls: 'Sibling', rate: '12%', color: C.gold };
    default: return { cls: 'Other', rate: '15%', color: C.burgundy };
  }
}

// ── Types ────────────────────────────────────────────────────
interface FamilyMember {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  date_of_death: string | null;
  relationship: string;
  generation: number;
  filing_status: string;
  state_of_residence: string | null;
  life_expectancy_override: number | null;
  is_us_citizen: boolean;
  notes: string | null;
}


// ── Member Form Modal ───────────────────────────────────────
const RELATIONSHIP_OPTIONS = [
  'patriarch', 'matriarch', 'spouse', 'child', 'grandchild', 'sibling', 'niece_nephew', 'other',
];
const FILING_OPTIONS = [
  { value: 'married_filing_jointly', label: 'Married Filing Jointly' },
  { value: 'married_filing_separately', label: 'Married Filing Separately' },
  { value: 'single', label: 'Single' },
];
const GENERATION_OPTIONS = [
  { value: 1, label: 'G1 — Principals' },
  { value: 2, label: 'G2 — Children' },
  { value: 3, label: 'G3 — Grandchildren' },
  { value: 4, label: 'Other' },
];
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DC','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

function MemberFormModal({ item, onSave, onClose }: {
  item: FamilyMember | null;
  onSave: (data: any) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    firstName: item?.first_name || '',
    lastName: item?.last_name || '',
    dateOfBirth: item?.date_of_birth ? String(item.date_of_birth).slice(0, 10) : '',
    dateOfDeath: item?.date_of_death ? String(item.date_of_death).slice(0, 10) : '',
    relationship: item?.relationship || 'child',
    generation: item?.generation ?? 2,
    filingStatus: item?.filing_status || 'married_filing_jointly',
    stateOfResidence: item?.state_of_residence || '',
    isUsCitizen: item?.is_us_citizen ?? true,
    lifeExpectancyOverride: item?.life_expectancy_override ?? '',
    notes: item?.notes || '',
  });

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = () => {
    if (!form.firstName || !form.lastName) return;
    onSave({
      firstName: form.firstName,
      lastName: form.lastName,
      dateOfBirth: form.dateOfBirth || undefined,
      dateOfDeath: form.dateOfDeath || undefined,
      relationship: form.relationship,
      generation: form.generation,
      filingStatus: form.filingStatus,
      stateOfResidence: form.stateOfResidence || undefined,
      isUsCitizen: form.isUsCitizen,
      lifeExpectancyOverride: form.lifeExpectancyOverride ? Number(form.lifeExpectancyOverride) : undefined,
      notes: form.notes || '',
    });
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', fontSize: 13, fontFamily: fonts.sans,
    border: `1px solid ${C.border}`, borderRadius: 3, background: 'white',
    outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 10, fontFamily: fonts.sans, fontWeight: 700,
    color: C.textLight, letterSpacing: '0.08em', textTransform: 'uppercase' as const, marginBottom: 4,
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}>
      <div style={{ background: 'white', borderRadius: 4, boxShadow: '0 8px 30px rgba(0,0,0,0.2)', width: '100%', maxWidth: 540, maxHeight: '90vh', overflowY: 'auto' as const, margin: '0 16px' }}>
        <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.borderLight}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: 18, fontFamily: fonts.display, fontWeight: 700, color: C.ink, margin: 0 }}>{item ? 'Edit Family Member' : 'Add Family Member'}</h3>
          <button onClick={onClose} style={{ fontSize: 20, color: C.textLight, background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
        </div>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column' as const, gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>First Name *</label>
              <input value={form.firstName} onChange={e => set('firstName', e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Last Name *</label>
              <input value={form.lastName} onChange={e => set('lastName', e.target.value)} style={inputStyle} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Relationship</label>
              <select value={form.relationship} onChange={e => set('relationship', e.target.value)} style={inputStyle}>
                {RELATIONSHIP_OPTIONS.map(r => (
                  <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1).replace(/_/g, '/')}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Generation</label>
              <select value={form.generation} onChange={e => set('generation', Number(e.target.value))} style={inputStyle}>
                {GENERATION_OPTIONS.map(g => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Date of Birth</label>
              <input type="date" value={form.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Date of Death</label>
              <input type="date" value={form.dateOfDeath} onChange={e => set('dateOfDeath', e.target.value)} style={inputStyle} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>State of Residence</label>
              <select value={form.stateOfResidence} onChange={e => set('stateOfResidence', e.target.value)} style={inputStyle}>
                <option value="">Select...</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Filing Status</label>
              <select value={form.filingStatus} onChange={e => set('filingStatus', e.target.value)} style={inputStyle}>
                {FILING_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 16 }}>
              <input type="checkbox" checked={form.isUsCitizen} onChange={e => set('isUsCitizen', e.target.checked)} id="citizen-cb" />
              <label htmlFor="citizen-cb" style={{ fontSize: 13, fontFamily: fonts.sans, color: C.textBody }}>U.S. Citizen</label>
            </div>
            <div>
              <label style={labelStyle}>Life Expectancy Override</label>
              <input type="number" value={form.lifeExpectancyOverride} onChange={e => set('lifeExpectancyOverride', e.target.value)} style={inputStyle} placeholder="Optional (years)" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} style={{ ...inputStyle, minHeight: 60 }} placeholder="Fiduciary roles, beneficiary designations, etc." />
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.borderLight}`, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button onClick={onClose} style={{ padding: '8px 18px', fontSize: 13, fontFamily: fonts.sans, fontWeight: 600, color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: 3, background: C.ivory, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSubmit} style={{ padding: '8px 18px', fontSize: 13, fontFamily: fonts.sans, fontWeight: 600, color: C.ivory, border: 'none', borderRadius: 3, background: C.navy, cursor: 'pointer' }}>
            {item ? 'Save Changes' : 'Add Member'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────
export default function FamilyMembersPage() {
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<{ open: boolean; item: FamilyMember | null }>({ open: false, item: null });

  useEffect(() => {
    async function load() {
      try {
        const res = await api.get('/members?limit=50');
        setMembers(res.data.data || []);
      } catch (e) {
        console.error('Failed to load family members:', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const reloadMembers = async () => {
    try {
      const res = await api.get('/members?limit=50');
      setMembers(res.data.data || []);
    } catch {}
  };
  const handleSave = async (data: any) => {
    try {
      if (modal.item) {
        await membersApi.update(modal.item.id, data);
      } else {
        await membersApi.create(data);
      }
      setModal({ open: false, item: null });
      await reloadMembers();
    } catch (err) { alert('Failed to save member'); }
  };
  const handleDelete = async (id: string) => {
    if (!confirm('Delete this family member? This may affect linked assets, gifts, and entities.')) return;
    try { await membersApi.delete(id); await reloadMembers(); } catch { alert('Failed to delete member'); }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
        <p style={{ fontFamily: fonts.sans, fontSize: 14, color: C.textMuted }}>Loading family members...</p>
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div>
        <PageHeader title="Family Members" subtitle="Principals, beneficiaries, and related parties" />
        <Card style={{ padding: 32, textAlign: 'center' }}>
          <p style={{ fontFamily: fonts.display, fontSize: 20, color: C.textMuted, marginBottom: 8 }}>No family members recorded</p>
          <p style={{ fontFamily: fonts.sans, fontSize: 13, color: C.textLight }}>Add family members via the Import wizard or create them manually.</p>
          <button onClick={() => setModal({ open: true, item: null })} style={{ marginTop: 16, padding: '8px 20px', fontSize: 13, fontFamily: fonts.sans, fontWeight: 600, borderRadius: 3, border: 'none', background: C.navy, color: 'white', cursor: 'pointer' }}>+ Add First Member</button>
        </Card>
        {modal.open && <MemberFormModal item={modal.item} onSave={handleSave} onClose={() => setModal({ open: false, item: null })} />}
      </div>
    );
  }

  // Group by generation
  const principals = members.filter(m => m.generation === 1);
  const children = members.filter(m => m.generation === 2);
  const grandchildren = members.filter(m => m.generation === 3);
  const others = members.filter(m => m.generation > 3 || m.generation < 1);

  // Stats
  const statesRepresented = [...new Set(members.map(m => m.state_of_residence).filter(Boolean))];
  const giftDonees = children.length + grandchildren.length;

  return (
    <div>
      {modal.open && <MemberFormModal item={modal.item} onSave={handleSave} onClose={() => setModal({ open: false, item: null })} />}
      <PageHeader title="Family Members" subtitle="Principals, beneficiaries, and related parties with estate planning roles" />

      {/* KPIs */}
      <Card style={{ display: 'flex', marginBottom: 24, overflow: 'hidden', flexWrap: 'wrap' }}>
        <Stat label="Principals" value={String(principals.length)} sub={principals.map(p => p.first_name).join(' & ')} accent={C.navy} />
        <Stat label="Children" value={String(children.length)} sub={children.map(c => c.first_name).join(', ')} accent={C.hunter} />
        <Stat label="Grandchildren" value={String(grandchildren.length)} sub="Annual exclusion donees" accent={C.gold} />
        <Stat label="Domicile States" value={String(statesRepresented.length)} sub={statesRepresented.join(', ')} />
      </Card>

      {/* Family Register Table */}
      <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ padding: '20px 24px 12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <SectionTitle sub="All individuals with entity ownership, fiduciary roles, or beneficiary status">Family Register</SectionTitle>
            <button onClick={() => setModal({ open: true, item: null })} style={{ padding: '6px 14px', fontSize: 11, fontFamily: fonts.sans, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' as any, borderRadius: 3, border: 'none', background: C.navy, color: 'white', cursor: 'pointer' }}>+ Add Member</button>
          </div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <TH>Name</TH>
              <TH>Relationship</TH>
              <TH>Age</TH>
              <TH>DOB</TH>
              <TH>Domicile</TH>
              <TH>Filing Status</TH>
              <TH>Citizen</TH>
              <TH></TH>
            </tr>
          </thead>
          <tbody>
            {/* G1 — Principals */}
            {principals.length > 0 && (
              <>
                <tr><td colSpan={8} style={{ padding: '10px 14px', background: C.parchment, fontFamily: fonts.sans, fontSize: 11, fontWeight: 700, color: C.navy, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{generationLabel(1)}</td></tr>
                {principals.map((m, i) => (
                  <tr key={m.id} style={{ background: i % 2 === 0 ? 'transparent' : C.cream }}>
                    <TD bold>{m.first_name} {m.last_name}</TD>
                    <TD>{relationshipLabel(m.relationship)}</TD>
                    <TD mono>{calcAge(m.date_of_birth) ?? '—'}</TD>
                    <TD>{formatDate(m.date_of_birth)}</TD>
                    <TD>{m.state_of_residence || '—'}</TD>
                    <TD><span style={{ fontSize: 11, color: C.textMuted }}>{m.filing_status?.replace(/_/g, ' ') || '—'}</span></TD>
                    <TD>{m.is_us_citizen ? <StatusPill status="current" label="Yes" /> : <StatusPill status="danger" label="No" />}</TD>
                    <TD>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => setModal({ open: true, item: m })} style={{ fontSize: 11, fontFamily: fonts.sans, color: C.navy, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                        <button onClick={() => handleDelete(m.id)} style={{ fontSize: 11, fontFamily: fonts.sans, color: C.burgundy, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Delete</button>
                      </div>
                    </TD>
                  </tr>
                ))}
              </>
            )}

            {/* G2 — Children */}
            {children.length > 0 && (
              <>
                <tr><td colSpan={8} style={{ padding: '10px 14px', background: C.parchment, fontFamily: fonts.sans, fontSize: 11, fontWeight: 700, color: C.hunter, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{generationLabel(2)}</td></tr>
                {children.map((m, i) => (
                  <tr key={m.id} style={{ background: i % 2 === 0 ? 'transparent' : C.cream }}>
                    <TD bold>{m.first_name} {m.last_name}</TD>
                    <TD>{relationshipLabel(m.relationship)}</TD>
                    <TD mono>{calcAge(m.date_of_birth) ?? '—'}</TD>
                    <TD>{formatDate(m.date_of_birth)}</TD>
                    <TD>{m.state_of_residence || '—'}</TD>
                    <TD><span style={{ fontSize: 11, color: C.textMuted }}>{m.filing_status?.replace(/_/g, ' ') || '—'}</span></TD>
                    <TD>{m.is_us_citizen ? <StatusPill status="current" label="Yes" /> : <StatusPill status="danger" label="No" />}</TD>
                    <TD>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => setModal({ open: true, item: m })} style={{ fontSize: 11, fontFamily: fonts.sans, color: C.navy, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                        <button onClick={() => handleDelete(m.id)} style={{ fontSize: 11, fontFamily: fonts.sans, color: C.burgundy, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Delete</button>
                      </div>
                    </TD>
                  </tr>
                ))}
              </>
            )}

            {/* G3 — Grandchildren */}
            {grandchildren.length > 0 && (
              <>
                <tr><td colSpan={8} style={{ padding: '10px 14px', background: C.parchment, fontFamily: fonts.sans, fontSize: 11, fontWeight: 700, color: C.gold, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{generationLabel(3)}</td></tr>
                {grandchildren.map((m, i) => (
                  <tr key={m.id} style={{ background: i % 2 === 0 ? 'transparent' : C.cream }}>
                    <TD bold>{m.first_name} {m.last_name}</TD>
                    <TD>{relationshipLabel(m.relationship)}</TD>
                    <TD mono>{calcAge(m.date_of_birth) ?? '—'}</TD>
                    <TD>{formatDate(m.date_of_birth)}</TD>
                    <TD>{m.state_of_residence || '—'}</TD>
                    <TD><span style={{ fontSize: 11, color: C.textMuted }}>{m.filing_status?.replace(/_/g, ' ') || '—'}</span></TD>
                    <TD>{m.is_us_citizen ? <StatusPill status="current" label="Yes" /> : <StatusPill status="danger" label="No" />}</TD>
                    <TD>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => setModal({ open: true, item: m })} style={{ fontSize: 11, fontFamily: fonts.sans, color: C.navy, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                        <button onClick={() => handleDelete(m.id)} style={{ fontSize: 11, fontFamily: fonts.sans, color: C.burgundy, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Delete</button>
                      </div>
                    </TD>
                  </tr>
                ))}
              </>
            )}

            {/* Others */}
            {others.length > 0 && (
              <>
                <tr><td colSpan={8} style={{ padding: '10px 14px', background: C.parchment, fontFamily: fonts.sans, fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Other</td></tr>
                {others.map((m, i) => (
                  <tr key={m.id} style={{ background: i % 2 === 0 ? 'transparent' : C.cream }}>
                    <TD bold>{m.first_name} {m.last_name}</TD>
                    <TD>{relationshipLabel(m.relationship)}</TD>
                    <TD mono>{calcAge(m.date_of_birth) ?? '—'}</TD>
                    <TD>{formatDate(m.date_of_birth)}</TD>
                    <TD>{m.state_of_residence || '—'}</TD>
                    <TD><span style={{ fontSize: 11, color: C.textMuted }}>{m.filing_status?.replace(/_/g, ' ') || '—'}</span></TD>
                    <TD>{m.is_us_citizen ? <StatusPill status="current" label="Yes" /> : <StatusPill status="danger" label="No" />}</TD>
                    <TD>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => setModal({ open: true, item: m })} style={{ fontSize: 11, fontFamily: fonts.sans, color: C.navy, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                        <button onClick={() => handleDelete(m.id)} style={{ fontSize: 11, fontFamily: fonts.sans, color: C.burgundy, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Delete</button>
                      </div>
                    </TD>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </Card>

      {/* Bottom panels */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* PA Inheritance Tax Class Map */}
        <Card style={{ padding: 24 }}>
          <SectionTitle sub="Pennsylvania inheritance tax classification by beneficiary relationship">Inheritance Tax Class Map</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginTop: 8 }}>
            {[
              { cls: 'Exempt', rate: '0%', rel: 'spouse', color: C.hunter },
              { cls: 'Direct Descendant', rate: '4.5%', rel: 'child/grandchild', color: C.navy },
              { cls: 'Sibling', rate: '12%', rel: 'sibling', color: C.gold },
              { cls: 'Other / Unrelated', rate: '15%', rel: 'other', color: C.burgundy },
            ].map((c, i) => {
              const count = members.filter(m => paClass(m.relationship).cls === c.cls).length;
              return (
                <div key={i} style={{ padding: '14px 16px', background: C.cream, borderRadius: 3, borderTop: `3px solid ${c.color}` }}>
                  <div style={{ fontFamily: fonts.sans, fontSize: 10, fontWeight: 700, color: C.textLight, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>{c.cls}</div>
                  <div style={{ fontFamily: fonts.display, fontSize: 22, fontWeight: 600, color: c.color, marginBottom: 4 }}>{c.rate}</div>
                  <div style={{ fontFamily: fonts.sans, fontSize: 12, color: C.textBody }}>{count} member{count !== 1 ? 's' : ''}</div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Planning Notes */}
        <Card style={{ padding: 24 }}>
          <SectionTitle>Planning Notes</SectionTitle>

          {/* Multi-state domicile alert */}
          {statesRepresented.length > 1 && (
            <AlertBox type="warning" title="Multi-State Domicile Exposure">
              Family members reside in {statesRepresented.length} states ({statesRepresented.join(', ')}). Beneficiaries domiciled in estate tax states may face additional state-level exposure on inherited intangible property. Verify domicile documentation for each member.
            </AlertBox>
          )}

          {/* Gift exclusion capacity */}
          <AlertBox type="info" title="Annual Exclusion Gift Capacity">
            With {giftDonees} eligible donees (children + grandchildren), a married couple can transfer up to ${((giftDonees * 19000 * 2) / 1000).toFixed(0)}K annually using gift splitting — no exemption consumed, no Form 709 required for exclusion gifts.
          </AlertBox>

          {/* Minor grandchildren */}
          {grandchildren.filter(g => (calcAge(g.date_of_birth) ?? 0) < 18).length > 0 && (
            <AlertBox type="info" title="Minor Beneficiaries">
              {grandchildren.filter(g => (calcAge(g.date_of_birth) ?? 0) < 18).length} grandchild{grandchildren.filter(g => (calcAge(g.date_of_birth) ?? 0) < 18).length !== 1 ? 'ren are' : ' is'} under 18. Gifts to minors may use §2503(c) trusts or UTMA accounts. Crummey withdrawal powers are required for trust gifts to qualify for annual exclusion.
            </AlertBox>
          )}

          {/* Non-citizen alert */}
          {members.some(m => !m.is_us_citizen) && (
            <AlertBox type="danger" title="Non-Citizen Beneficiary">
              One or more family members are not U.S. citizens. The annual gift exclusion to a non-citizen spouse is $194,000 (2026), and the unlimited marital deduction does not apply. QDOT may be required for estate tax deferral.
            </AlertBox>
          )}

          {/* Notes from individual records */}
          {members.filter(m => m.notes).map(m => (
            <AlertBox key={m.id} type="info" title={`${m.first_name} ${m.last_name}`}>
              {m.notes}
            </AlertBox>
          ))}
        </Card>
      </div>
    </div>
  );
}
